import {
  CONFIG_OPTION_TOAST_ERROR
} from "common/toast";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AsyncPaginate, wrapMenuList } from 'react-select-async-paginate';
import { components } from "react-select";
import { toast } from "react-toastify";
import { ceil } from "lodash";
import plansApi from "api/planApi";
import { formatNumberWithCommas } from "helpers/format";

interface Option {
  label: string | any;
  value: string;
}
interface DropdownOptionProps {
  name?: string;
  listCheckedOption?: string | null;
  debounceTimeout?: number;
  onChangeSelect?: (params: Option) => void;
  isMulti?: boolean;
  placeholder?: string;
  isShowIconCheck?: boolean;
  hideSelectedOptions?: boolean;
  isAllOption?: boolean;
  isDisabled?: boolean,
  keywordSearch?: string,
  isClearable?: boolean,
  defaultParams?: any,
  showPricePlan?: boolean,
}
const LIMIT = 30

const DropdownAsyncPlan = ({
  name = "",
  listCheckedOption,
  debounceTimeout = 700,
  onChangeSelect,
  isMulti = false,
  placeholder,
  isShowIconCheck = false,
  hideSelectedOptions = true,
  isAllOption = false,
  isDisabled = false,
  keywordSearch = '',
  isClearable = true,
  defaultParams = {},
  showPricePlan=false
}: DropdownOptionProps) => {
  const { t, i18n } = useTranslation();
  // const [isAllCheckedUsers, setIsAllCheckedUsers] = useState(false);

  const OPTION_ALL_DEFAULT = { label: t("All Filter"), value: "all" };

  const [options, setOptions] = useState([])

  const setOnChangeOption = (item: any) => {
    onChangeSelect && onChangeSelect(item);
  }

  const loadListPlans = async ({
    page,
    limit,
    search
  }: {
    page: number
    limit: number
    search?: string
  }) => {
    let params: any = { page, limit, keyword: search, ...defaultParams };

    try {
      const { data: { items, pagination }, code } = await plansApi.getPlans(params);
      if (!code && code !== 200) {
        return { success: false };
      }

      const dataPlanOptions = items?.map((a: any) => {
        return ({
          value: (a?.id),
          label: (
            <div className="d-flex align-items-center gap-2">
              <div>
                {
                  showPricePlan ? (
                    <>
                      {`${a?.info?.name ? a?.info?.name : ''} - `} 
                      <small className={`${a?.priceType === "FREE" ? "text-success" : "text-danger"}`}>{a?.priceType === "FREE" ? "Free" : formatNumberWithCommas(a?.price?.priceFinal)}</small>
                    </>
                  ) : (
                    <>
                      {`${a?.info?.name ? a?.info?.name : '-'} - `} <small className="text-secondary">{a?.code}</small>
                    </>
                  )
                }
              </div>
            </div>
          ),
          data: a,
        })
      })
      return {
        code,
        dataPlanOptions: dataPlanOptions || [],
        lastPage: ceil(pagination?.total / pagination?.limit)
      };
    } catch (e) {
      return {};
    }
  }

  async function loadOptions(search: string, loadedOptions: any[], { page }: { page: number }) {
    const {
      code,
      dataPlanOptions,
      lastPage
    } = await loadListPlans({ page, limit: LIMIT, search });
    let dataPlanOptionsFormat = dataPlanOptions || [];

    if (code !== 200) {
      toast(`${t("Failed to load data")}`, CONFIG_OPTION_TOAST_ERROR);
      return {
        options: [],
        hasMore: false,
      };
    }
    let lastPageFormat = lastPage ? lastPage : 1;
    let options = dataPlanOptionsFormat;

    setOptions(options)

    return {
      options: options,
      hasMore: lastPageFormat > page,
      additional: {
        page: page + 1,
      },
    };
  }

  const MenuList = (props: any) => {
    let childComponent = props?.children;
    if (props?.children?.length > 0) {
      const allOption = props?.children?.filter((item: any) => item?.props?.value === OPTION_ALL_DEFAULT.value);
      let fixedIndex = -1;
      if (allOption) {
        fixedIndex = props?.children.indexOf(allOption?.[0]);
      }
      const fixedElement = props?.children?.[fixedIndex];

      const childComponentFormat = ((fixedIndex != -1 ? props?.children.slice(1) : props?.children) || [])?.sort((a: any, b: any) => {
        let pointA = a.props.isSelected === true ? 1 : 0;
        let pointB = b.props.isSelected === true ? 1 : 0;
        return pointB - pointA;
      })

      if (fixedElement) {
        childComponent = [fixedElement, ...childComponentFormat]
      } else {
        childComponent = childComponentFormat;
      }
    }

    return (
      <components.MenuList {...props}>{childComponent}</components.MenuList>
    );
  }

  let isAllCheckedUsers = false;
  const Option = (props: any) => {
    if (props?.isSelected && props?.value === OPTION_ALL_DEFAULT.value) {
      isAllCheckedUsers = true
    } else if (!props?.isSelected && props?.value === OPTION_ALL_DEFAULT.value) {
      isAllCheckedUsers = false;
    }

    return (
      <components.Option {...props} style={{ zIndex: 999 }}>
        <div className="d-flex align-items-center gap-2">
          {((props?.isSelected || isAllCheckedUsers) && isShowIconCheck) ? (
            <i className="ri-check-line align-middle fs-14" />
          ) : (
            ""
          )}
          {props.data?.label}
        </div>
      </components.Option>
    );
  }

  const MenuListAsync = wrapMenuList(MenuList);

  useEffect(() => {
    if (keywordSearch) {
      setOptions([])
      loadOptions(keywordSearch, [], { page: 1 })
    }
  }, [keywordSearch]);

  const optionsMemo = useMemo(() => {
    return [...(isAllOption ? [OPTION_ALL_DEFAULT] : []), ...options];
  }, [JSON.stringify(options), isAllOption, i18n?.language]);

  useEffect(() => {
    if (((listCheckedOption as any)?.value === OPTION_ALL_DEFAULT.value || !(listCheckedOption as any)?.value) && isAllOption) {
      setOnChangeOption(OPTION_ALL_DEFAULT)
    }
  }, [i18n.language, isAllOption, JSON.stringify(listCheckedOption)]);

  return (
    <AsyncPaginate
      key={i18n.language}
      name={name}
      isMulti={isMulti}
      isClearable={isClearable}
      // defaultOptions
      options={optionsMemo}
      debounceTimeout={debounceTimeout}
      value={listCheckedOption}
      loadOptions={loadOptions}
      placeholder={placeholder}
      className="dropdown-status-rounded"
      hideSelectedOptions={false}
      onChange={(e: any) => setOnChangeOption(e)}
      additional={{
        page: 1,
      }}
      components={{
        Option: Option,
        ...(isMulti ? { MenuList: MenuListAsync } : null)
        // MenuList: MenuListAsync,
      }}
      closeMenuOnSelect={hideSelectedOptions}
      menuPortalTarget={document.body}
      styles={{
        option: (styles: any) => {
          let styleNew = isMulti ? {
            ...styles,
            backgroundColor: "white",
            zIndex: 99,
            color: "black",
          } : styles;

          return styleNew;
        },
        control: (styles: any, { isFocused }: any) => {
          return {
            ...styles,
            borderColor: "#e9ebec",
            borderRadius: "0.6rem",
            color: "white",
            "&:hover": { borderColor: "#009ef7" },
          };
        },
        multiValue: (styles: any) => {
          return {
            ...styles,
            backgroundColor: "#009ef7",
          };
        },
        multiValueLabel: (styles: any) => ({
          ...styles,
          color: "white !important",
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 1051,
        }),
      }}
      isDisabled={isDisabled}
    />
  );
};

export default DropdownAsyncPlan;
